import { SWISS_MADE_SOFTWARE_ENABLED, SWISS_MADE_SOFTWARE_URL } from '../config/marketing'
import { useLandingLocale, type Multilingual } from '../lib/marketingLocale'

/**
 * Six-way because the badge sits in the landing footer and the pricing section.
 * Unlike the nav and the footer this component takes no `locale` prop — its
 * language has always followed the visitor rather than the route, which is
 * correct on the landing and unobservable on a document, since the whole
 * component renders `null` until the label is granted.
 */
const CAPTION: Multilingual<string> = {
  en: 'Certified Swiss software label',
  de: 'Zertifiziertes Schweizer Software-Label',
  fr: 'Label certifié de logiciel suisse',
  it: 'Marchio certificato di software svizzero',
  es: 'Sello certificado de software suizo',
  pt: 'Selo certificado de software suíço',
  hu: 'Tanúsított svájci szoftvercímke',
  nl: 'Gecertificeerd Zwitsers softwarekeurmerk',
  pl: 'Certyfikowana szwajcarska etykieta oprogramowania',
  cs: 'Certifikovaná švýcarská softwarová značka',
}

/**
 * The "swiss made software" label slot.
 *
 * Renders NOTHING until the label is actually granted — see
 * SWISS_MADE_SOFTWARE_ENABLED. The slot exists now so that turning the label on
 * is a one-constant change rather than a layout job under launch pressure.
 *
 * The link back to swissmadesoftware.org is their usage requirement, not a
 * decoration: the mark may not be shown without it.
 */
export function SwissMadeBadge({ className = '', height = 40 }: { className?: string; height?: number }) {
  const locale = useLandingLocale()
  if (!SWISS_MADE_SOFTWARE_ENABLED) return null

  return (
    <a
      href={SWISS_MADE_SOFTWARE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center transition-opacity hover:opacity-80 ${className}`}
      title={CAPTION[locale]}
    >
      <img
        src="/badges/swiss-made-software.svg"
        alt={CAPTION[locale]}
        height={height}
        style={{ height }}
        loading="lazy"
        decoding="async"
      />
    </a>
  )
}
